import { fg } from '@atlaskit/platform-feature-flags';
import { SORT_FIELD_KEY } from '@atlassian/search-client';
import { getBackendExperiment } from '@atlassian/search-experiment';

import {
	DEFAULT_ENTITY_ATI,
	RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS,
	RESULT_LIMIT_FOR_SINGLE_PRODUCT,
} from '../../../common/constants/entities';
import { SEARCH_PAGE } from '../../../common/constants/experience';
import {
	PrimaryProductKeys,
	productsSupportingEmptyQuery,
} from '../../../common/constants/products';
import type { BootstrapVariables, SearchVariables } from '../../../common/constants/relay/types';
import { getSessionIdFromLocalStorage } from '../../../common/utils/events/utils';
import { serve3PClickIngestionExperiment } from '../../../common/utils/experiments';
import { isHello } from '../../../common/utils/is-hello';
import { getSelectedFilters } from '../../../controllers/filters/selected';
import { getJiraFiltersForResultsCountQuery } from '../../../controllers/results/utils';
import { serve3pGroupingExperiment } from '../../../controllers/store/bootstrap';
import type { SelectedInputFilter } from '../../../controllers/store/dropdown';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { rightPanelQuery$variables } from '../right-panel/__generated__/rightPanelQuery.graphql';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { searchResultsQuery$variables } from '../search-results/__generated__/searchResultsQuery.graphql';

/**
 * Gets the variables for both search results and right panel queries.
 */
export const getVariables = (
	bootstrap: BootstrapVariables,
	search: SearchVariables,
	selectedInputFilters: SelectedInputFilter[],
	overrideSearchSessionId?: string | null,
): [searchResultsQuery$variables, rightPanelQuery$variables] => {
	const {
		cloudId = '',
		primaryProduct,
		thirdPartyConfigs,
		experimentConfig,
		products,
		allFilterExcludedProducts,
		productEdition,
		productDataRegions,
		isJiraIssueIndexed,
		user,
	} = bootstrap;
	const {
		searchQuery,
		originalQuery,
		resultsSort: sort,
		filters,
		selectedProducts,
		selectedDatasource,
		jsmIncidentFilterIds,
		paginationTokens,
	} = search;

	const searchSessionId = overrideSearchSessionId || bootstrap.searchSessionId;
	const fetchFromCursor = paginationTokens[0]?.cursor;

	const isEmptyQuery = !searchQuery || searchQuery.trim() === '';
	const availableProducts = products.filter(
		// If the query is empty, only some products are valid
		(product) => !isEmptyQuery || productsSupportingEmptyQuery.includes(product),
	);

	const {
		selectedFilters,
		entities,
		unselectedProductsThirdPartyFilters,
		entitiesForResultsCountQuery,
	} = getSelectedFilters({
		filters,
		selectedProducts,
		selectedDatasource,
		availableProducts: products,
		allFilterExcludedProducts,
		thirdPartyConfigs,
		jsmIncidentFilterIds,
		currentUserId: user?.id,
	});

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience: SEARCH_PAGE,
		tenantId: cloudId,
		isJiraIssueIndexed,
		thirdPartyFilters: selectedFilters.thirdPartyFilters,
	});

	const { experimentId, shadowExperimentId, experimentLayers } = backendExperiment || {};

	const limit =
		entities.length === DEFAULT_ENTITY_ATI.length
			? RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS
			: RESULT_LIMIT_FOR_SINGLE_PRODUCT;

	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
		talentFilters,
	} = selectedFilters;

	const sortVariables = sort.field === SORT_FIELD_KEY.RELEVANCE ? [] : [sort];

	// If the original query param is present, we want to disable query replacement
	const disableQueryReplacement = !!originalQuery;

	const queryVersion = 1;
	const cloudIdARI =
		primaryProduct === PrimaryProductKeys.Jira && fg('jira_site_ari_query_enabled')
			? `ari:cloud:jira::site/${cloudId}`
			: `ari:cloud:confluence::site/${cloudId}`;
	const experience = SEARCH_PAGE;
	const tenantId = cloudId;

	const sessionId = getSessionIdFromLocalStorage();

	const grouping3pCohort = serve3pGroupingExperiment(true);

	const fanoutExperimentCohort = (() => {
		if (grouping3pCohort === 'control') {
			return selectedProducts.length === 0 ? grouping3pCohort : undefined;
		}

		if (grouping3pCohort === 'disable-v1' || grouping3pCohort === 'enable-v2') {
			return grouping3pCohort;
		}
		// Fallback to existing logic
		return selectedProducts.length === 0 ? 'boosted-and-smartlinks' : undefined;
	})();

	// To ensure rightPanel query matches as much as possible, we inherit+override both from shared variables
	const sharedQueryVariables = {
		cloudIdARI,
		commonFilters,
		confluenceFilters,
		entities,
		experience,
		jiraFilters,
		limit,
		mercuryFilters,
		compassFilters,
		query: searchQuery,
		queryVersion,
		searchSessionId,
		sort: sortVariables,
		sourceProduct: primaryProduct,
		thirdPartyFilters,
		sessionId,
		tenantId,
		disableQueryReplacement,
		...(fg('assets_rovo_search_all') ? { assetsFilters } : {}),
		...(fg('talent_global_search') ? { talentFilters } : {}),
	};

	const searchResultsQueryVariables: searchResultsQuery$variables = {
		...sharedQueryVariables,
		// pagination is only valid for results
		after: fetchFromCursor,
		// highlighting is only useful for results
		enableHighlighting: true,
		// Experimentation is only valid in results section. Note that experiments can differ based on selected entites/products, so if we include experiments in counts, the counts will change as filters change
		experimentId,
		experimentLayers: experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
			name,
			layerId,
			shadowId,
			definitions,
		})),
		shadowExperimentId,
		fanoutExperimentCohort,
		includeAssigneeField: fg('rovo_search_assignee_field_fps'),
		includeIconUrl: fg('rovo_search_confluence_icons_fps'),
		includePositions: fg('talent_global_search'),
		includeBoardObject: fg('rovo_search_update_board_url'),
		includeThirdPartyId: serve3PClickIngestionExperiment({ fireExperimentExposure: false }),

		// It's recommended to use a Variable Provider to conditionally fetch data.
		// See this runbook for details: https://hello.atlassian.net/wiki/x/yx_OWgE
	};

	const rightPanelQueryVariables: rightPanelQuery$variables = {
		...sharedQueryVariables,
		// Limit and sort are irrelevant for counts
		limit: 0,
		sort: [],
		// Counts searches for approx all entities (see getSelectedFilters for edge cases)
		entities: entitiesForResultsCountQuery,
		thirdPartyFilters: unselectedProductsThirdPartyFilters,
		// We need to search for all jira product types to support counts in Jira/JSM/JPD correctly
		jiraFilters: getJiraFiltersForResultsCountQuery(jiraFilters, availableProducts),
	};

	return [searchResultsQueryVariables, rightPanelQueryVariables];
};
